import { useState } from 'react';
import { ComparisonResult, ProductResult } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ComparisonPanelProps {
  originalProducts: ProductResult[];
  onReExecute: () => void;
  isExecuting: boolean;
}

const ComparisonPanel = ({ originalProducts, onReExecute, isExecuting }: ComparisonPanelProps) => {
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  const handleReExecute = async () => {
    onReExecute();
    
    setTimeout(() => {
      const mockComparisons: ComparisonResult[] = originalProducts.map(product => ({
        productId: product.id,
        changes: [
          {
            field: 'ICMS',
            originalValue: product.taxCalculation.icms.rate,
            currentValue: product.taxCalculation.icms.rate + (Math.random() * 2 - 1),
            percentageChange: (Math.random() * 10 - 5),
            changeType: Math.random() > 0.5 ? 'increase' : 'decrease'
          },
          {
            field: 'IPI',
            originalValue: product.taxCalculation.ipi.rate,
            currentValue: product.taxCalculation.ipi.rate,
            changeType: 'no_change'
          }
        ],
        impactAnalysis: {
          totalDifference: (Math.random() * 1000 - 500),
          percentageImpact: (Math.random() * 5 - 2.5),
          recommendation: Math.random() > 0.7 
            ? 'Requer aprovação gerencial devido à variação significativa'
            : 'Variação dentro dos limites aceitáveis',
          requiresApproval: Math.random() > 0.7
        }
      }));
      
      setComparisonResults(mockComparisons);
      setShowComparison(true);
    }, 2000);
  };

  const getChangeColor = (changeType: string) => {
    switch (changeType) {
      case 'increase':
        return 'text-error bg-error/10';
      case 'decrease':
        return 'text-success bg-success/10';
      case 'no_change':
        return 'text-muted-foreground bg-muted';
      case 'regulatory_update':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Comparação com API Atual</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Execute novamente a consulta para comparar com dados atualizados
          </p>
        </div>
        <Button
          variant="default"
          iconName="RefreshCw"
          iconPosition="left"
          loading={isExecuting}
          onClick={handleReExecute}
        >
          {isExecuting ? 'Executando...' : 'Re-executar Consulta'}
        </Button>
      </div>

      {isExecuting && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">
              Consultando API Gestor Tributário 3.0...
            </p>
          </div>
        </div>
      )}

      {showComparison && !isExecuting && comparisonResults.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={20} color="var(--color-error)" />
                <span className="text-sm font-medium text-muted-foreground">Aumentos</span>
              </div>
              <p className="text-2xl font-semibold text-error">
                {comparisonResults.filter(r => 
                  r.changes.some(c => c.changeType === 'increase')
                ).length}
              </p>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingDown" size={20} color="var(--color-success)" />
                <span className="text-sm font-medium text-muted-foreground">Reduções</span>
              </div>
              <p className="text-2xl font-semibold text-success">
                {comparisonResults.filter(r => 
                  r.changes.some(c => c.changeType === 'decrease')
                ).length}
              </p>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                <span className="text-sm font-medium text-muted-foreground">Requer Aprovação</span>
              </div>
              <p className="text-2xl font-semibold text-warning">
                {comparisonResults.filter(r => r.impactAnalysis.requiresApproval).length}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {comparisonResults.map((result, index) => {
              const product = originalProducts.find(p => p.id === result.productId);
              if (!product) return null;

              return (
                <div key={result.productId} className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-foreground">{product.description}</h3>
                      <p className="text-sm text-muted-foreground font-mono mt-1">
                        Código: {product.code}
                      </p>
                    </div>
                    {result.impactAnalysis.requiresApproval && (
                      <span className="px-3 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
                        Aprovação Necessária
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.changes.map((change, changeIndex) => (
                      <div key={changeIndex} className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase">
                          {change.field}
                        </label>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="text-sm text-muted-foreground">Original</p>
                            <p className="text-base font-mono text-foreground">
                              {typeof change.originalValue === 'number' 
                                ? `${change.originalValue.toFixed(2)}%`
                                : change.originalValue}
                            </p>
                          </div>
                          <Icon 
                            name={change.changeType === 'increase' ? 'TrendingUp' : 
                                  change.changeType === 'decrease' ? 'TrendingDown' : 'Minus'} 
                            size={20} 
                            color={change.changeType === 'increase' ? 'var(--color-error)' :
                                   change.changeType === 'decrease' ? 'var(--color-success)' :
                                   'var(--color-muted-foreground)'}
                          />
                          <div className="flex-1">
                            <p className="text-sm text-muted-foreground">Atual</p>
                            <p className="text-base font-mono text-foreground">
                              {typeof change.currentValue === 'number' 
                                ? `${change.currentValue.toFixed(2)}%`
                                : change.currentValue}
                            </p>
                          </div>
                        </div>
                        {change.percentageChange !== undefined && (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getChangeColor(change.changeType)}`}>
                            {change.percentageChange > 0 ? '+' : ''}{change.percentageChange.toFixed(2)}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="pt-3 border-t border-border">
                    <div className="flex items-start gap-3">
                      <Icon name="Info" size={18} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-foreground">{result.impactAnalysis.recommendation}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>
                            Diferença: {formatCurrency(Math.abs(result.impactAnalysis.totalDifference))}
                          </span>
                          <span>
                            Impacto: {result.impactAnalysis.percentageImpact > 0 ? '+' : ''}
                            {result.impactAnalysis.percentageImpact.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!showComparison && !isExecuting && (
        <div className="text-center py-12">
          <Icon name="GitCompare" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            Execute a consulta novamente para visualizar comparações
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonPanel;